import React, { useState, useEffect, useRef } from 'react';

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

const sections = [
  {
    id: 's1', number: '01', title: 'Information Collection',
    content: 'We may collect personal details such as your name, email address, and any other information you provide when you contact us, use our services, or fill out forms on our website.',
  },
  {
    id: 's2', number: '02', title: 'How We Use Your Information',
    content: null,
    list: [
      'Provide and improve our services',
      'Respond to customer inquiries',
      'Send occasional updates or promotional content (only if you opt in)',
    ],
    note: 'We do not sell, rent, or share your personal information with third parties for marketing purposes.',
  },
  {
    id: 's3', number: '03', title: 'Data Security',
    content: 'We implement strong technical and organizational measures to protect your personal information from unauthorized access, misuse, or disclosure.',
  },
  {
    id: 's4', number: '04', title: 'Your Rights',
    content: 'You may request to view, correct, or delete any personal data we hold about you at any time by contacting us.',
  },
  {
    id: 's5', number: '05', title: 'Changes to This Policy',
    content: 'We may update this policy from time to time. We encourage you to review this page periodically for any changes.',
  },
  {
    id: 's6', number: '06', title: 'Contact Us',
    content: null, cta: true,
  },
];

/* ── Renders one section's body content ── */
const SectionBody = ({ sec }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    {sec.content && (
      <p style={{ fontSize: 15, lineHeight: 1.8, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>
        {sec.content}
      </p>
    )}

    {sec.list && (
      <>
        <p style={{ fontSize: 15, lineHeight: 1.8, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>
          The data we collect is used to:
        </p>
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, padding: 0, margin: 0 }}>
          {sec.list.map((item) => (
            <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 15, lineHeight: 1.7, color: '#8b9bb4', fontWeight: 300 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#2dd4bf', flexShrink: 0, marginTop: 8 }} />
              {item}
            </li>
          ))}
        </ul>
      </>
    )}

    {sec.note && (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', borderRadius: 10, background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.15)', fontSize: 14, color: '#8b9bb4', lineHeight: 1.7 }}>
        <span style={{ color: '#2dd4bf', flexShrink: 0, fontSize: 16, marginTop: 1 }}>◈</span>
        {sec.note}
      </div>
    )}

    {sec.cta && (
      <>
        <p style={{ fontSize: 15, lineHeight: 1.8, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>
          If you have questions or concerns about this privacy policy, please reach out via our contact page. We aim to respond within 24 hours.
        </p>
        <a
          href="/contact"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 22px', background: 'rgba(45,212,191,0.1)', border: '1px solid rgba(45,212,191,0.25)', borderRadius: 9, color: '#2dd4bf', textDecoration: 'none', fontSize: 14, fontWeight: 600, alignSelf: 'flex-start' }}
        >
          Go to Contact Page →
        </a>
      </>
    )}
  </div>
);

const PrivacyPolicy1 = () => {
  const w        = useWindowWidth();
  const isMobile = w <= 600;
  const isTablet = w <= 900;
  const px       = isMobile ? '18px' : isTablet ? '28px' : '40px';

  const [active, setActive] = useState('s1');

  /* ── Desktop scroll-spy via IntersectionObserver ── */
  const clickLock = useRef(null);
  const lockTimer = useRef(null);

  useEffect(() => {
    if (isMobile) return; // mobile uses tab pattern — no spy needed

    const observers = [];
    sections.forEach((sec) => {
      const el = document.getElementById(sec.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !clickLock.current) {
            setActive(sec.id);
          }
        },
        { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [isMobile]);

  /* ── Desktop: scroll to section on sidebar click ── */
  const scrollTo = (id) => {
    setActive(id);
    clickLock.current = id;
    clearTimeout(lockTimer.current);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    lockTimer.current = setTimeout(() => { clickLock.current = null; }, 1000);
  };

  /* ── Mobile: just switch active tab, no scrolling needed ── */
  const selectSection = (id) => {
    setActive(id);
  };

  const activeSec = sections.find((s) => s.id === active);

  const sidebarBtnStyle = (id) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 14px', width: '100%',
    border: 'none',
    borderLeft: `3px solid ${active === id ? '#2dd4bf' : 'transparent'}`,
    borderRadius: '0 8px 8px 0',
    fontSize: 13,
    fontWeight: active === id ? 600 : 400,
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer', textAlign: 'left',
    background: active === id ? 'rgba(45,212,191,0.09)' : 'transparent',
    color: active === id ? '#2dd4bf' : '#4a5568',
    transition: 'all 0.2s ease',
  });

  return (
    <main
      className="page-enter"
      style={{ padding: '0 0 80px', maxWidth: 1160, margin: '0 auto', width: '100%' }}
    >
      <div style={{ padding: isMobile ? `56px ${px} 40px` : `80px ${px} 60px` }}>

        {/* ── Page Header ── */}
        <header style={{ marginBottom: 40, maxWidth: 680, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="section-label">Legal</div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? '30px' : 'clamp(32px,4vw,52px)', fontWeight: 700, color: '#f0f4f8', letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0 }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>
            At <strong style={{ color: '#2dd4bf' }}>Apps ViT Limited</strong>, we are committed to
            protecting your privacy. This policy explains what we collect, how we use it, and how we keep it safe.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <span style={{ fontSize: 12, color: '#4a5568', fontWeight: 500 }}>Last updated: March 2025</span>
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#4a5568', display: 'inline-block' }} />
            <span style={{ fontSize: 12, color: '#4a5568', fontWeight: 500 }}>6 sections</span>
          </div>
        </header>

        {/* ════════════════════════════════════════
            MOBILE LAYOUT — Tab selector + content panel
            ════════════════════════════════════════ */}
        {isMobile && (
          <div>
            {/* Section tab pills — scrollable row */}
            <div style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              paddingBottom: 12,
              marginBottom: 20,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',          /* Firefox */
              msOverflowStyle: 'none',         /* IE */
            }}>
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => selectSection(sec.id)}
                  style={{
                    flexShrink: 0,
                    padding: '8px 14px',
                    border: `1px solid ${active === sec.id ? '#2dd4bf' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: active === sec.id ? 600 : 400,
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: 'pointer',
                    background: active === sec.id ? 'rgba(45,212,191,0.12)' : 'rgba(255,255,255,0.03)',
                    color: active === sec.id ? '#2dd4bf' : '#8b9bb4',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: 5 }}>{sec.number}</span>
                  {sec.title}
                </button>
              ))}
            </div>

            {/* Active section content card */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16,
              padding: '24px 20px',
            }}>
              {/* Section heading */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'rgba(45,212,191,0.12)',
                  fontSize: 10, fontWeight: 700, color: '#2dd4bf',
                }}>
                  {activeSec.number}
                </span>
                <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 600, color: '#f0f4f8', margin: 0, letterSpacing: '-0.01em' }}>
                  {activeSec.title}
                </h2>
              </div>

              {/* Body */}
              <SectionBody sec={activeSec} />

              {/* Prev / Next navigation */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {sections.findIndex(s => s.id === active) > 0 ? (
                  <button
                    onClick={() => selectSection(sections[sections.findIndex(s => s.id === active) - 1].id)}
                    style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, background: 'transparent', color: '#8b9bb4', fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' }}
                  >
                    ← Prev
                  </button>
                ) : <span />}

                <span style={{ fontSize: 12, color: '#4a5568', alignSelf: 'center' }}>
                  {sections.findIndex(s => s.id === active) + 1} / {sections.length}
                </span>

                {sections.findIndex(s => s.id === active) < sections.length - 1 ? (
                  <button
                    onClick={() => selectSection(sections[sections.findIndex(s => s.id === active) + 1].id)}
                    style={{ padding: '8px 16px', border: '1px solid rgba(45,212,191,0.25)', borderRadius: 8, background: 'rgba(45,212,191,0.08)', color: '#2dd4bf', fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' }}
                  >
                    Next →
                  </button>
                ) : <span />}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════
            DESKTOP LAYOUT — Sticky sidebar + full article
            ════════════════════════════════════════ */}
        {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: '56px', alignItems: 'start' }}>

            {/* Sticky sidebar */}
            <aside style={{ position: 'sticky', top: 90 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4a5568', marginBottom: 12, paddingLeft: 14 }}>
                Contents
              </div>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {sections.map((sec) => (
                  <button key={sec.id} onClick={() => scrollTo(sec.id)} style={sidebarBtnStyle(sec.id)}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                      background: active === sec.id ? 'rgba(45,212,191,0.15)' : 'rgba(255,255,255,0.04)',
                      fontSize: 9, fontWeight: 700,
                      color: active === sec.id ? '#2dd4bf' : '#4a5568',
                    }}>
                      {sec.number}
                    </span>
                    {sec.title}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Full article — all sections visible, spy tracks position */}
            <article style={{ display: 'flex', flexDirection: 'column' }}>
              {sections.map((sec) => (
                <section
                  key={sec.id}
                  id={sec.id}
                  style={{ paddingBottom: 52, scrollMarginTop: 90, display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 13, fontWeight: 600, color: 'rgba(45,212,191,0.5)', letterSpacing: '0.04em' }}>{sec.number}</span>
                    <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(17px,2.2vw,24px)', fontWeight: 600, color: '#f0f4f8', letterSpacing: '-0.01em', margin: 0 }}>{sec.title}</h2>
                  </div>
                  <SectionBody sec={sec} />
                  <div style={{ height: 1, background: 'linear-gradient(90deg,rgba(255,255,255,0.07),transparent)', marginTop: 8 }} />
                </section>
              ))}
            </article>

          </div>
        )}

      </div>
    </main>
  );
};

export default PrivacyPolicy1;